import { useEffect } from 'react';
import { useTenantConfig } from './useTenantConfig';

/**
 * Convert hex color (#rrggbb) to bare HSL string "H S% L%" for CSS variables
 * that are consumed via hsl(var(--varname)) in tailwind.
 */
function hexToHsl(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) {
    return `0 0% ${Math.round(l * 100)}%`;
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

const CSS_VAR_MAP: Record<string, { varName: string; format: 'hsl' | 'hex' }> = {
  primary_color: { varName: '--primary', format: 'hsl' },
  secondary_color: { varName: '--secondary', format: 'hsl' },
  chart_1_color: { varName: '--chart-1', format: 'hex' },
  chart_2_color: { varName: '--chart-2', format: 'hex' },
  chart_3_color: { varName: '--chart-3', format: 'hex' },
  chart_4_color: { varName: '--chart-4', format: 'hex' },
  chart_5_color: { varName: '--chart-5', format: 'hex' },
};

function applyWhitelabeling(wl: Record<string, any> | undefined) {
  const root = document.documentElement;

  for (const [field, { varName, format }] of Object.entries(CSS_VAR_MAP)) {
    const value = wl?.[field] as string | undefined;
    if (value) {
      root.style.setProperty(varName, format === 'hsl' ? hexToHsl(value) : value);
    } else {
      root.style.removeProperty(varName);
    }
  }

  document.title = wl?.page_title || 'Pliable';

  const faviconLink = document.querySelector<HTMLLinkElement>('link[rel="icon"][sizes="32x32"]');
  if (faviconLink) {
    faviconLink.href = wl?.favicon_path || '/favicon/favicon-32x32.png';
  }
}

// Apply whitelabeling from __PLB_ENV__ immediately (before React mounts)
// to prevent flicker on custom domains
const envWhitelabeling = (window as any).__PLB_ENV__?.whitelabeling;
if (envWhitelabeling) {
  applyWhitelabeling(envWhitelabeling);
}

export function useWhitelabelOverrides() {
  const { data: tenantConfig } = useTenantConfig();

  useEffect(() => {
    const wl = tenantConfig?.whitelabeling;
    const isEnabled = wl?.enabled !== false;
    applyWhitelabeling(isEnabled ? wl : undefined);
  }, [tenantConfig]);
}
